import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import {
  History,
  Loader2,
  Search,
  User,
  Calendar,
  ArrowRight,
  X,
  ExternalLink,
  Filter
} from 'lucide-react'

type EntityTable = 'units' | 'lessons' | 'contents' | 'exercises'

interface EditRow {
  id: string
  entity_table: EntityTable
  entity_id: string
  field_name: string
  old_value: string | null
  new_value: string | null
  edited_at: string
  edited_by: string
  editor: { first_name: string; last_name: string; email: string } | null
  // Resuelto en cliente:
  entity_label?: string
  entity_edit_url?: string
}

const TABLE_LABEL: Record<EntityTable, string> = {
  units: 'Unidad',
  lessons: 'Lección',
  contents: 'Contenido',
  exercises: 'Ejercicio'
}

const TABLE_COLOR: Record<EntityTable, string> = {
  units: 'bg-blue-100 text-blue-700',
  lessons: 'bg-green-100 text-green-700',
  contents: 'bg-purple-100 text-purple-700',
  exercises: 'bg-orange-100 text-orange-700'
}

function getEntityEditUrl(table: EntityTable, id: string): string {
  return `/admin/${table}/${id}/edit`
}

function truncate(s: string | null, max = 80): string {
  if (!s) return '(vacío)'
  if (s.length <= max) return s
  return s.slice(0, max) + '…'
}

function stripHtml(html: string | null): string {
  if (!html) return ''
  return html.replace(/<[^>]+>/g, ' ').replaceAll(/\s+/g, ' ').trim()
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

export default function EditHistory() {
  const [rows, setRows] = useState<EditRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editorFilter, setEditorFilter] = useState<string>('all')
  const [tableFilter, setTableFilter] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [detailRow, setDetailRow] = useState<EditRow | null>(null)

  useEffect(() => {
    loadHistory()
  }, [])

  const loadHistory = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const { data, error: histErr } = await (supabase
        .from('entity_edit_history') as any)
        .select('*, editor:profiles!edited_by(first_name, last_name, email)')
        .order('edited_at', { ascending: false })
        .limit(500)

      if (histErr) throw histErr
      const baseRows: EditRow[] = (data || []) as EditRow[]

      // Enriquecer con título de la entidad para dar contexto.
      const idsByTable: Record<EntityTable, Set<string>> = {
        units: new Set(), lessons: new Set(), contents: new Set(), exercises: new Set()
      }
      for (const r of baseRows) idsByTable[r.entity_table]?.add(r.entity_id)

      const labelMap: Record<string, string> = {}

      const fetches: Promise<void>[] = []
      const pushFetch = (table: EntityTable, ids: string[], builder: (row: any) => string) => {
        if (ids.length === 0) return
        fetches.push(
          Promise.resolve(
            supabase.from(table).select('id, order_num, title')
              .in('id', ids)
              .then(({ data: rs }) => {
                for (const r of (rs as any[] || [])) {
                  labelMap[`${table}:${r.id}`] = builder(r)
                }
              })
          )
        )
      }
      pushFetch('units', Array.from(idsByTable.units),
        r => `Unidad ${r.order_num ?? '?'}: ${r.title || ''}`)
      pushFetch('lessons', Array.from(idsByTable.lessons),
        r => `Lección ${r.order_num ?? '?'}: ${r.title || ''}`)
      pushFetch('contents', Array.from(idsByTable.contents),
        r => `Contenido ${r.order_num ?? '?'}: ${r.title || ''}`)
      pushFetch('exercises', Array.from(idsByTable.exercises),
        r => `Ejercicio ${r.order_num ?? '?'}`)

      await Promise.all(fetches)

      const enriched: EditRow[] = baseRows.map(r => ({
        ...r,
        entity_label: labelMap[`${r.entity_table}:${r.entity_id}`] || r.entity_id,
        entity_edit_url: getEntityEditUrl(r.entity_table, r.entity_id)
      }))

      setRows(enriched)
    } catch (e: any) {
      setError(e?.message || 'Error al cargar historial')
    } finally {
      setIsLoading(false)
    }
  }

  // Lista única de editores presentes en los datos
  const editors = useMemo(() => {
    const map = new Map<string, { id: string; name: string; email: string }>()
    for (const r of rows) {
      if (!map.has(r.edited_by)) {
        const name = r.editor
          ? `${r.editor.first_name || ''} ${r.editor.last_name || ''}`.trim() || r.editor.email
          : r.edited_by
        map.set(r.edited_by, { id: r.edited_by, name, email: r.editor?.email || '' })
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [rows])

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (editorFilter !== 'all' && r.edited_by !== editorFilter) return false
      if (tableFilter !== 'all' && r.entity_table !== tableFilter) return false
      if (search) {
        const term = search.toLowerCase()
        const haystack = [
          r.entity_label, r.field_name,
          stripHtml(r.old_value), stripHtml(r.new_value),
          r.editor?.first_name, r.editor?.last_name, r.editor?.email
        ].filter(Boolean).join(' ').toLowerCase()
        if (!haystack.includes(term)) return false
      }
      return true
    })
  }, [rows, editorFilter, tableFilter, search])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <History className="w-6 h-6 text-amber-500" />
          Historial de cambios
        </h1>
        <p className="text-gray-600 mt-1">
          Cada edición hecha desde el modo edición del Super Prof queda registrada con valor anterior y nuevo.
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar en texto, entidad, campo, editor…"
            className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={editorFilter}
            onChange={e => setEditorFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">Todos los editores</option>
            {editors.map(ed => (
              <option key={ed.id} value={ed.id}>{ed.name}</option>
            ))}
          </select>

          <select
            value={tableFilter}
            onChange={e => setTableFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">Todos los tipos</option>
            <option value="units">Unidades</option>
            <option value="lessons">Lecciones</option>
            <option value="contents">Contenidos</option>
            <option value="exercises">Ejercicios</option>
          </select>
        </div>
      </div>

      {/* Listado */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border">
          <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay cambios registrados con esos filtros.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => {
            const editorName = r.editor
              ? `${r.editor.first_name || ''} ${r.editor.last_name || ''}`.trim() || r.editor.email
              : r.edited_by
            return (
              <button
                key={r.id}
                onClick={() => setDetailRow(r)}
                className="w-full text-left bg-white rounded-xl border p-4 hover:border-amber-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${TABLE_COLOR[r.entity_table]}`}>
                    {TABLE_LABEL[r.entity_table]}
                  </span>
                  <span className="font-medium text-gray-900 text-sm">
                    {r.entity_label}
                  </span>
                  <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
                    .{r.field_name}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 items-start text-sm mb-2">
                  <div className="bg-red-50 border border-red-100 rounded p-2 text-red-900 line-through opacity-80">
                    {truncate(stripHtml(r.old_value), 120)}
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 mt-2 hidden md:block" />
                  <div className="bg-green-50 border border-green-100 rounded p-2 text-green-900">
                    {truncate(stripHtml(r.new_value), 120)}
                  </div>
                </div>

                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    {editorName}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDateTime(r.edited_at)}
                  </span>
                </div>
              </button>
            )
          })}

          <p className="text-xs text-gray-400 text-center mt-4">
            Mostrando {filtered.length} de {rows.length} cambios{rows.length === 500 ? ' (límite alcanzado)' : ''}.
          </p>
        </div>
      )}

      {/* Modal de detalle */}
      {detailRow && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setDetailRow(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${TABLE_COLOR[detailRow.entity_table]}`}>
                    {TABLE_LABEL[detailRow.entity_table]}
                  </span>
                  <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
                    .{detailRow.field_name}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {detailRow.entity_label}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {detailRow.entity_edit_url && (
                  <Link
                    to={detailRow.entity_edit_url}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                    onClick={() => setDetailRow(null)}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ir al item
                  </Link>
                )}
                <button
                  onClick={() => setDetailRow(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-red-600 mb-1 uppercase">Antes</p>
                  <pre className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-900 whitespace-pre-wrap break-words">
                    {detailRow.old_value || '(vacío)'}
                  </pre>
                </div>
                <div>
                  <p className="text-xs font-semibold text-green-600 mb-1 uppercase">Después</p>
                  <pre className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900 whitespace-pre-wrap break-words">
                    {detailRow.new_value || '(vacío)'}
                  </pre>
                </div>
              </div>

              <div className="text-xs text-gray-500 border-t pt-3 flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {detailRow.editor
                    ? `${detailRow.editor.first_name || ''} ${detailRow.editor.last_name || ''}`.trim() || detailRow.editor.email
                    : detailRow.edited_by}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDateTime(detailRow.edited_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
